import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  FormHelperText,
  Grid,
  Link,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";

import { fetcherAuth } from "utils/api";
import { REFERRAL_CREDIT } from "utils/influencer";
import { formatEpochTimestamp } from "utils/time";
import { trackEvent } from "utils/tracking";

import styles from "./styles";

type ReferralCredit = {
  id: number;
  createdAt: number;
  description: string;
  amount: number; // in cents
};

interface Props {
  hideBalance?: boolean;
  source?: string;
}

export default function Referral({
  hideBalance,
  source = "Settings Page",
}: Props) {
  const theme = useTheme();
  const { currentOrg } = useContext(OrganizationUserContext);
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { referralCode } = useContext(SubscriptionContext);

  const { getToken } = useAuth();
  const { setAlert, setErrorAlert } = useContext(AlertContext);
  const [balance, setBalance] = useState<number>(0);
  const [referrals, setReferrals] = useState<ReferralCredit[]>([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [viewReferrals, setViewReferrals] = useState(false);
  const [total, setTotal] = useState(0);

  const fetchCustomerBalance = async () => {
    setFetchLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/payments/customer/balance`,
      );
      setBalance(res.balance);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setFetchLoading(false);
    }
  };

  const fetchTransactions = async (creditId?: number) => {
    try {
      let url = `/api/organization/${currentOrg?.id}/referrals/credits?per_page=10`;
      if (creditId) {
        url += `&cursor=${creditId}`;
      }
      const res = await fetcherAuth(getToken, url);
      setReferrals((prev) => [...prev, ...res.credits]);
      setTotal(res.total);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setFetchLoading(false);
    }
  };

  const handleCopyToClipboard = () => {
    if (referralCode) {
      navigator.clipboard.writeText(referralCode);
    }

    trackEvent("Referral Link Copied to Clipboard", { source });

    if (setAlert) setAlert("Copied to clipboard!", "success");
  };

  useEffect(() => {
    if (currentOrg?.id) {
      fetchCustomerBalance();
      fetchTransactions();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  const formatBalance = (balance: number) => {
    // Balance is in cents. Since balance is in negative if we owe the user
    // we need to reverse it for UI to show them as credit amount.
    return Number((balance / 100) * -1)?.toFixed(2);
  };

  const align = source === "Settings Page" ? "left" : "center";

  return (
    <>
      {isMobileScreen && (
        <Typography textAlign={align} variant="h3" sx={{ mt: 2 }}>
          🎁
        </Typography>
      )}

      <Grid container columnGap={2} rowGap={2} alignItems="flex-start">
        <Grid item xs={12} lg>
          <Typography variant="h4" paragraph textAlign={align}>
            Want free credits on Bento? 🎁
          </Typography>

          <Typography sx={{ mr: 4 }} textAlign={align}>
            Refer friend, earn credits! <br />
            <br />
            Give ${REFERRAL_CREDIT} to a new user. Get ${REFERRAL_CREDIT} when
            they upgrade.{" "}
            <Link
              target="_blank"
              rel="noreferrer"
              href="https://storage.googleapis.com/hatchways-crm.appspot.com/app-assets/Bento%20Referral%20Program%20T%2BC's%20-%20Google%20Docs.pdf"
              sx={{ color: "primary.main", cursor: "pointer" }}
              onClick={() =>
                trackEvent("Referral Terms and Conditions Clicked", {
                  source,
                })
              }
            >
              T&Cs apply
            </Link>
            <br />
            <br />
          </Typography>

          <Typography
            variant="caption"
            color="textSecondary"
            paragraph
            textAlign={align}
          >
            Here's your unique referral link:
          </Typography>

          <Grid container justifyContent={align}>
            <Box sx={styles.referralCode} onClick={handleCopyToClipboard}>
              <Box sx={styles.code}>{referralCode}</Box>
              <Box
                component="i"
                sx={{ lineHeight: "1.25" }}
                className="fa-regular fa-copy fa-lg"
              />
            </Box>
          </Grid>
        </Grid>

        {!hideBalance && (
          <Grid item xs={12} lg>
            <Grid item sx={styles.balance}>
              <Typography variant="h6">💰 Current Balance</Typography>
              <FormHelperText>
                This balance will be automatically applied towards your next
                invoice.
              </FormHelperText>
              {fetchLoading ? (
                <Skeleton height={100} width={"50%"} />
              ) : (
                <Typography variant="h2" sx={{ my: 2 }}>
                  ${balance ? formatBalance(balance) : "0.00"} USD
                </Typography>
              )}

              {referrals?.length > 0 && (
                <Link
                  sx={{
                    cursor: "pointer",
                    textDecoration: "none",
                    fontSize: 12,
                  }}
                  onClick={() => {
                    setViewReferrals((prev) => !prev);
                    trackEvent("My Referrals Button Clicked");
                  }}
                >
                  <strong>{viewReferrals ? "Hide" : "My Referrals"}</strong>
                </Link>
              )}

              {viewReferrals && referrals && (
                <Table size="small" sx={{ maxWidth: 500 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {referrals.map((row) => (
                      <TableRow
                        key={row.id}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <TableCell>
                          {formatEpochTimestamp({
                            epochTimestamp: row.createdAt,
                            addYear: true,
                          })}
                        </TableCell>
                        <TableCell>{row.description}</TableCell>
                        <TableCell>{formatBalance(row.amount * -1)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              {total > referrals?.length && viewReferrals && (
                <Button
                  onClick={() =>
                    fetchTransactions(referrals?.[referrals?.length - 1]?.id)
                  }
                  size="small"
                  sx={{ fontSize: 12, textTransform: "none" }}
                  color="secondary"
                >
                  View more
                </Button>
              )}
            </Grid>
          </Grid>
        )}
      </Grid>
    </>
  );
}
